/**
 * Error provider for managing global error state
 *
 * This provider manages global error state and provides error handling
 * utilities throughout the application.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  type JSX,
} from "react";
import type { BaseError } from "@charphq/errors";
import { ErrorToast } from "../components/error/error-toast";

interface ErrorContextType {
  error: BaseError | null;
  setError: (error: BaseError | null) => void;
  clearError: () => void;
  showError: (error: BaseError) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
  autoHide?: boolean;
  autoHideDelay?: number;
}

export function ErrorProvider({
  children,
  autoHide = true,
  autoHideDelay = 5000,
}: ErrorProviderProps): JSX.Element {
  const [error, setError] = useState<BaseError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const showError = useCallback((newError: BaseError) => {
    setError(newError);
  }, []);

  // Memoized so consumers only re-render when the error actually changes —
  // a fresh object every provider render would invalidate the whole subtree.
  const value = useMemo<ErrorContextType>(
    () => ({
      error,
      setError,
      clearError,
      showError,
    }),
    [error, setError, clearError, showError],
  );

  return (
    <ErrorContext.Provider value={value}>
      {children}
      <ErrorToast
        error={error}
        onDismiss={clearError}
        autoHide={autoHide}
        autoHideDelay={autoHideDelay}
      />
    </ErrorContext.Provider>
  );
}

export function useErrorContext(): ErrorContextType {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error("useErrorContext must be used within an ErrorProvider");
  }
  return context;
}
